import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Plus, Filter, LayoutGrid, List as ListIcon, Clock, ChevronDown, Check,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLeads, LeadsStore } from '../lib/leadsStore';
import { useTeam } from '../lib/teamStore';
import { useAuth } from '../lib/authStore';
import LoanStageBadge, { LOAN_STAGES, LOAN_STAGE_KEYS, normalizeStageKey } from '../components/LoanStageBadge';
import { cn } from '../lib/utils';
import FundedConfirmModal from '../components/pipeline/FundedConfirmModal';
import { DatePopover } from '../components/ui/date-popover';

// Pretty Loan Purpose label.
function loanPurposeLabel(lead) {
  const raw = lead?.application?.loanPurpose || lead?.loanIntent || '';
  if (!raw) return '—';
  if (/^[a-z_]+$/.test(raw)) return raw.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  return raw;
}

function money(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return '—';
  return `$${v.toLocaleString('en-US')}`;
}

const lenderFor = (l) => (l?.application?.lender || l?.lender || '').trim();

// Channel options. Per-deal selection now lives on the row itself.
const CHANNELS = [
  { value: 'broker',        label: 'Broker' },
  { value: 'correspondent', label: 'Correspondent' },
];

const MONTHS = [
  { v: 0,  short: 'Jan', long: 'January' },
  { v: 1,  short: 'Feb', long: 'February' },
  { v: 2,  short: 'Mar', long: 'March' },
  { v: 3,  short: 'Apr', long: 'April' },
  { v: 4,  short: 'May', long: 'May' },
  { v: 5,  short: 'Jun', long: 'June' },
  { v: 6,  short: 'Jul', long: 'July' },
  { v: 7,  short: 'Aug', long: 'August' },
  { v: 8,  short: 'Sep', long: 'September' },
  { v: 9,  short: 'Oct', long: 'October' },
  { v: 10, short: 'Nov', long: 'November' },
  { v: 11, short: 'Dec', long: 'December' },
];

export default function Pipeline() {
  const leads = useLeads();
  const team = useTeam();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [view, setView] = useState('kanban');
  // Pipeline defaults to "all" so the LO sees their entire pipeline.
  // Toggle to `month` to bucket by Estimated Closing Date.
  const [periodMode, setPeriodMode] = useState('all'); // 'all' | 'month'
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  // ── Assigned-LO scope ────────────────────────────────────────────────
  // `scope` = 'mine' | 'all' | '<user_id>'
  //   'mine' = pipeline deals assigned to the signed-in user (default,
  //            matches the "give me MY deals first" ergonomic that both
  //            LOs and admins want when they open Pipeline).
  //   'all'  = every LO's pipeline combined (admin oversight).
  //   uuid   = a single LO's pipeline, picked from the Filters popover.
  // LOs are always locked to 'mine' — the leadsStore never fetches other
  // LOs' leads for them anyway, but we hide the picker to avoid confusion.
  const [scope, setScope] = useState('mine');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersBtnRef = useRef(null);
  const filtersPopRef = useRef(null);

  // Close the Filters popover when clicking outside.
  useEffect(() => {
    if (!filtersOpen) return;
    const onClick = (e) => {
      if (filtersPopRef.current?.contains(e.target)) return;
      if (filtersBtnRef.current?.contains(e.target)) return;
      setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [filtersOpen]);

  // Pipeline = non-funded leads in any stage, excluding withdrawn or
  // denied loans (those drop out of the active funnel entirely).
  const pipelineLeads = useMemo(() => {
    return leads.filter((l) => {
      if (l.withdrawn || l.denied) return false;
      const key = normalizeStageKey(l.loanStatus);
      if (!key) return false;
      if (key === 'funded') return false;
      if (!LOAN_STAGE_KEYS.includes(key)) return false;
      // Scope filter — 'all' shows everyone (admin only); 'mine' scopes to
      // the current user; anything else is a specific LO's user_id.
      const effectiveScope = isAdmin ? scope : 'mine';
      if (effectiveScope === 'all') return true;
      const owner = effectiveScope === 'mine' ? user?.id : effectiveScope;
      return l.assignedTo === owner;
    });
  }, [leads, scope, user?.id, isAdmin]);

  // Period filter — bucketed by `estimatedClosingDate`. Deals without an
  // ECD are treated as "unbucketed" and only show in the All view.
  const visible = useMemo(() => {
    if (periodMode === 'all') return pipelineLeads;
    return pipelineLeads.filter((l) => {
      if (!l.estimatedClosingDate) return false;
      const d = new Date(l.estimatedClosingDate);
      if (isNaN(d)) return false;
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [pipelineLeads, periodMode, year, month]);

  const totalVolume = useMemo(
    () => visible.reduce((sum, l) => sum + (Number(l.estAmount) || 0), 0),
    [visible],
  );
  const years = useMemo(() => {
    const set = new Set();
    pipelineLeads.forEach((l) => {
      if (!l.estimatedClosingDate) return;
      const d = new Date(l.estimatedClosingDate);
      if (!isNaN(d)) set.add(d.getFullYear());
    });
    set.add(now.getFullYear());
    return Array.from(set).sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineLeads]);

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] text-neutral-500">Workspace / Pipeline</div>
          <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Pipeline</h1>
          <p className="text-[13px] text-neutral-500">
            {visible.length} deals · <span className="font-semibold text-neutral-700 tabular-nums">${(totalVolume / 1_000_000).toFixed(2)}M</span> total volume
            {periodMode === 'month' && (
              <> · projected close in <span className="font-semibold text-neutral-700">{MONTHS[month].long} {year}</span></>
            )}
            {isAdmin && scope !== 'mine' && (
              <> · <span className="font-semibold text-neutral-900" data-testid="pipeline-scope-label">
                {scope === 'all' ? 'All loan officers' : (team.find((m) => m.id === scope)?.name || 'Selected LO')}
              </span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodPicker
            mode={periodMode}
            year={year}
            month={month}
            years={years}
            onChange={(next) => {
              if (next.mode !== undefined) setPeriodMode(next.mode);
              if (next.year !== undefined) setYear(next.year);
              if (next.month !== undefined) setMonth(next.month);
            }}
          />
          <div className="flex items-center gap-1 rounded-lg border hairline p-0.5 text-[11px] bg-white">
            {[
              { v: 'kanban', label: 'Kanban', Icon: LayoutGrid },
              { v: 'list',   label: 'List',   Icon: ListIcon },
            ].map(({ v, label, Icon }) => (
              <button
                key={v}
                data-testid={`pipeline-view-${v}`}
                onClick={() => setView(v)}
                className={cn(
                  'px-2.5 py-1 rounded-md font-semibold flex items-center gap-1',
                  view === v ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900',
                )}
              >
                <Icon className="h-3 w-3" /> {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <button
              ref={filtersBtnRef}
              onClick={() => setFiltersOpen((o) => !o)}
              className={cn(
                'h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1.5',
                filtersOpen && 'border-neutral-400 bg-neutral-50',
                isAdmin && scope !== 'mine' && 'border-neutral-900 bg-neutral-900 text-white hover:border-neutral-900',
              )}
              data-testid="pipeline-filters-btn"
            >
              <Filter className="h-3.5 w-3.5" /> Filters
              {isAdmin && scope !== 'mine' && (
                <span className="ml-1 text-[10px] font-bold uppercase tracking-wider">· 1</span>
              )}
            </button>
            {filtersOpen && (
              <div
                ref={filtersPopRef}
                className="absolute right-0 top-full mt-2 w-[280px] rounded-xl border hairline bg-white shadow-2xl p-3 z-30"
                data-testid="pipeline-filters-popover"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-400 mb-2">
                  Show pipeline for
                </div>
                {!isAdmin ? (
                  <div className="text-[11.5px] text-neutral-500 px-2 py-3 bg-neutral-50 rounded-md">
                    Only administrators can view other LO&apos;s pipelines. Your pipeline shows all deals assigned to you.
                  </div>
                ) : (
                  <div className="space-y-1 max-h-[320px] overflow-y-auto">
                    <ScopeRow current={scope} value="mine" label="My pipeline" sub="Deals assigned to you" onPick={setScope} testId="scope-mine" />
                    <ScopeRow current={scope} value="all"  label="All loan officers" sub="Firm-wide view" onPick={setScope} testId="scope-all" />
                    <div className="pt-2 pb-1 px-2 text-[10px] uppercase tracking-wider font-bold text-neutral-400">Individual LOs</div>
                    {team
                      .filter((m) => m.is_active !== false && m.id !== user?.id)
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((m) => (
                        <ScopeRow
                          key={m.id}
                          current={scope}
                          value={m.id}
                          label={m.name || m.email}
                          sub={m.role === 'admin' ? 'Administrator' : 'Loan Officer'}
                          onPick={setScope}
                          testId={`scope-lo-${m.id}`}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button className="h-9 px-3 rounded-lg btn-ink text-[12.5px] font-semibold flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> New Deal
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyPipeline mode={periodMode} monthLabel={`${MONTHS[month].long} ${year}`} />
      ) : view === 'list' ? (
        <PipelineList leads={visible} />
      ) : (
        <PipelineKanban leads={visible} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ScopeRow — a selectable row inside the Filters popover for picking */
/* which LO's pipeline (or all) to view. Admin-only in practice.      */
/* ------------------------------------------------------------------ */
function ScopeRow({ current, value, label, sub, onPick, testId }) {
  const isActive = current === value;
  return (
    <button
      onClick={() => onPick(value)}
      data-testid={testId}
      className={cn(
        'w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 transition-colors',
        isActive ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100 text-neutral-800',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold truncate">{label}</div>
        <div className={cn('text-[10.5px] truncate', isActive ? 'text-white/70' : 'text-neutral-500')}>{sub}</div>
      </div>
      {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* PeriodPicker — All / Month with year+month selection. Drives the   */
/* `estimatedClosingDate` filter on the pipeline.                     */
/* ------------------------------------------------------------------ */
function PeriodPicker({ mode, year, month, years, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const label = mode === 'all' ? 'All Months' : `${MONTHS[month].long} ${year}`;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="pipeline-period-trigger"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold flex items-center gap-2',
          mode === 'month' ? 'text-neutral-900 border-neutral-900' : 'text-neutral-700',
        )}
      >
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">Closing</span>
        <span className="text-neutral-900">{label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="pipeline-period-menu"
          className="absolute right-0 top-full mt-1.5 z-30 w-[300px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden p-3"
        >
          {/* All Months toggle */}
          <button
            data-testid="pipeline-period-all"
            onClick={() => { onChange({ mode: 'all' }); setOpen(false); }}
            className={cn(
              'w-full text-left px-3 py-2 rounded-md text-[12.5px] font-bold transition mb-2',
              mode === 'all' ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-50 text-neutral-700',
            )}
          >
            {mode === 'all' ? '✓ ' : ''}All Months
          </button>
          <div className="border-t hairline-soft my-2" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">Filter by closing month</span>
          </div>
          {/* Year row */}
          <div className="flex items-center gap-1 mb-2">
            {years.map((y) => (
              <button
                key={y}
                data-testid={`pipeline-period-year-${y}`}
                onClick={() => onChange({ mode: 'month', year: y })}
                className={cn(
                  'h-7 px-2.5 rounded-md text-[12px] font-bold tabular-nums transition',
                  mode === 'month' && y === year
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-700 hover:bg-neutral-100',
                )}
              >
                {y}
              </button>
            ))}
          </div>
          {/* Month grid */}
          <div className="grid grid-cols-4 gap-1.5">
            {MONTHS.map((m) => {
              const active = mode === 'month' && m.v === month;
              return (
                <button
                  key={m.v}
                  data-testid={`pipeline-period-month-${m.v}`}
                  onClick={() => onChange({ mode: 'month', month: m.v })}
                  className={cn(
                    'h-8 rounded-md text-[12px] font-bold transition',
                    active ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:bg-neutral-100',
                  )}
                >
                  {m.short}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                          */
/* ------------------------------------------------------------------ */
function EmptyPipeline({ mode, monthLabel }) {
  if (mode === 'month') {
    return (
      <div className="rounded-2xl border hairline bg-white px-8 py-16 text-center" data-testid="pipeline-empty">
        <div className="mx-auto h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
          <ChevronLeft className="h-5 w-5 text-neutral-400" />
          <ChevronRight className="h-5 w-5 text-neutral-400" />
        </div>
        <h2 className="display text-[20px] font-bold text-neutral-900">Nothing closing in {monthLabel}</h2>
        <p className="text-[13px] text-neutral-500 mt-1 max-w-md mx-auto">
          Set an <span className="font-semibold text-neutral-700">Estimated Closing Date</span> on a deal's Loan Terms and it will show up in the right month.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border hairline bg-white px-8 py-16 text-center" data-testid="pipeline-empty">
      <div className="mx-auto h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
        <LayoutGrid className="h-5 w-5 text-neutral-400" />
      </div>
      <h2 className="display text-[20px] font-bold text-neutral-900">No deals in pipeline yet</h2>
      <p className="text-[13px] text-neutral-500 mt-1 max-w-md mx-auto">
        Open a lead and set the loan stage to <span className="font-semibold text-neutral-700">Ready to Submit</span>.
        It will move out of <Link to="/leads" className="font-semibold text-neutral-900 underline underline-offset-2">Leads</Link> and land here automatically.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* List view — Name | Stage | Lender | Loan Purpose | Loan Amount | Channel */
/* ------------------------------------------------------------------ */
function PipelineList({ leads }) {
  // Sort state — clicking any column header cycles asc → desc → asc.
  // The default surface sort is `closeDate desc` so the most recent close
  // dates float to the top regardless of insertion order. Switching keys
  // resets direction back to "natural" for that column.
  const [sortKey, setSortKey] = useState('closeDate');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    const list = [...leads];
    const cmp = (a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'name':       av = (a.name || '').toLowerCase();         bv = (b.name || '').toLowerCase();         break;
        case 'stage':      av = LOAN_STAGE_KEYS.indexOf(normalizeStageKey(a.loanStatus));
                           bv = LOAN_STAGE_KEYS.indexOf(normalizeStageKey(b.loanStatus));                        break;
        case 'lender':     av = lenderFor(a).toLowerCase();           bv = lenderFor(b).toLowerCase();           break;
        case 'loanNumber': av = (a.loanNumber || '').toLowerCase();   bv = (b.loanNumber || '').toLowerCase();   break;
        case 'purpose':    av = loanPurposeLabel(a).toLowerCase();    bv = loanPurposeLabel(b).toLowerCase();    break;
        case 'amount':     av = Number(a.estAmount) || 0;             bv = Number(b.estAmount) || 0;             break;
        case 'closeDate':  av = a.estimatedClosingDate || '';         bv = b.estimatedClosingDate || '';         break;
        case 'channel':    av = (a.loanChannel || '~').toLowerCase(); bv = (b.loanChannel || '~').toLowerCase(); break;
        default:           av = 0; bv = 0;
      }
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1);
    };
    list.sort(cmp);
    return list;
  }, [leads, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // Column meta — keeps header + body in lockstep. `align` matches the
  // cell's text alignment so the active-sort arrow lines up next to the
  // label rather than floating off-center.
  const COLS = [
    { key: 'name',       label: 'Name',        align: 'left' },
    { key: 'stage',      label: 'Stage',       align: 'left' },
    { key: 'lender',     label: 'Lender',      align: 'left' },
    { key: 'loanNumber', label: 'Loan #',      align: 'left' },
    { key: 'purpose',    label: 'Purpose',     align: 'left' },
    { key: 'amount',     label: 'Loan Amount', align: 'right' },
    { key: 'closeDate',  label: 'Close Date',  align: 'left' },
    { key: 'channel',    label: 'Channel',     align: 'center' },
  ];

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden">
      {/* 8-col fractional grid. Close Date now sits to the right of Loan
          Amount per spec. Every header is a clickable sort toggle. */}
      <div className="grid grid-cols-[2.4fr_1.6fr_1.6fr_1fr_1fr_1.4fr_1.2fr_1fr] gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b hairline bg-neutral-50/70">
        {COLS.map((c) => (
          <SortHeader
            key={c.key}
            label={c.label}
            align={c.align}
            active={sortKey === c.key}
            dir={sortDir}
            onClick={() => onSort(c.key)}
            testId={`pipeline-sort-${c.key}`}
          />
        ))}
      </div>
      <div className="divide-y divide-neutral-100">
        {sorted.map((l) => (
          <PipelineRow key={l.id} lead={l} />
        ))}
      </div>
    </div>
  );
}

function SortHeader({ label, align, active, dir, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold transition-colors',
        align === 'right' && 'justify-end pr-4 w-full',
        align === 'center' && 'justify-center w-full',
        active ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900',
      )}
    >
      <span>{label}</span>
      {active ? (
        dir === 'asc' ? <ArrowUp className="h-2.5 w-2.5" strokeWidth={3} />
                      : <ArrowDown className="h-2.5 w-2.5" strokeWidth={3} />
      ) : (
        <ArrowUpDown className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60 transition-opacity" strokeWidth={2.5} />
      )}
    </button>
  );
}

function PipelineRow({ lead }) {
  const lender = lenderFor(lead);
  const loanNumber = (lead.loanNumber || '').trim();
  return (
    <div
      data-testid={`pipeline-row-${lead.id}`}
      className="grid grid-cols-[2.4fr_1.6fr_1.6fr_1fr_1fr_1.4fr_1.2fr_1fr] gap-3 px-5 py-3.5 items-center hover:bg-neutral-50/60 transition-colors"
    >
      <div className="min-w-0">
        <Link
          to={`/leads/${lead.id}`}
          className="text-[14px] font-bold text-neutral-900 hover:underline underline-offset-2 truncate block"
        >
          {lead.name}
        </Link>
        <div className="text-[11.5px] text-neutral-500 truncate mt-0.5">
          {lead.application?.loanPurpose ? loanPurposeLabel(lead) : (lead.source || '—')}
          {lead.city ? ` · ${lead.city}` : ''}
        </div>
      </div>
      <div>
        <StagePicker lead={lead} />
      </div>
      <div className="text-[13px] truncate" title={lender || 'Lender will appear once added in the loan terms'}>
        {lender ? (
          <span className="text-neutral-700 font-semibold">{lender}</span>
        ) : (
          <span className="text-neutral-400 italic">Not set</span>
        )}
      </div>
      <div className="text-[12.5px] tabular-nums font-semibold truncate" title={loanNumber || 'Loan number not yet assigned'}>
        {loanNumber ? (
          <span className="text-neutral-700">{loanNumber}</span>
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </div>
      <div className="text-[13px] text-neutral-700 truncate" title={loanPurposeLabel(lead)}>
        {loanPurposeLabel(lead)}
      </div>
      <div className="text-right pr-4 text-[14px] font-bold text-neutral-900 tabular-nums">
        {money(lead.estAmount)}
      </div>
      <div>
        <CloseDateCell lead={lead} />
      </div>
      <div className="flex justify-center">
        <ChannelCell lead={lead} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* CloseDateCell — inline editor for `estimatedClosingDate`. Clicking */
/* swaps the chip for the shared <DatePopover> so brokers can either  */
/* type the date or pick from the calendar. Blur or pick saves.       */
/* ------------------------------------------------------------------ */
function CloseDateCell({ lead }) {
  const [editing, setEditing] = useState(false);
  const wrapRef = React.useRef(null);

  // Close the editor when focus leaves the inline wrapper (the calendar
  // popover renders in a portal, so its clicks don't count as "outside").
  React.useEffect(() => {
    if (!editing) return;
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      const inWrap = wrapRef.current.contains(e.target);
      const inCal = e.target.closest('[data-radix-popper-content-wrapper]');
      if (!inWrap && !inCal) setEditing(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [editing]);

  // Display format: "Mar 22, '26". Parsed manually so a bare YYYY-MM-DD
  // doesn't get interpreted as UTC midnight and roll back a day in PST.
  const display = useMemo(() => {
    const raw = lead.estimatedClosingDate;
    if (!raw) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (isNaN(d)) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  }, [lead.estimatedClosingDate]);

  const save = async (next) => {
    const current = (lead.estimatedClosingDate || '').slice(0, 10);
    const cleaned = (next || '').slice(0, 10);
    if (cleaned === current) return;
    const updated = await LeadsStore.update(lead.id, { estimatedClosingDate: cleaned || null });
    if (updated) {
      toast.success(cleaned ? `${updated.name} closing ${cleaned}` : `${updated.name} close date cleared`);
    } else {
      toast.error('Could not update close date');
    }
  };

  if (editing) {
    return (
      <div ref={wrapRef} className="w-[170px]" data-testid={`pipeline-closedate-input-${lead.id}`}>
        <DatePopover
          testId={`pipeline-closedate-popover-${lead.id}`}
          value={(lead.estimatedClosingDate || '').slice(0, 10)}
          onChange={(iso) => { save(iso); setEditing(false); }}
          placeholder="mm/dd/yyyy"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`pipeline-closedate-${lead.id}`}
      onClick={() => setEditing(true)}
      className={cn(
        'inline-flex items-center h-7 px-2 rounded-md text-[12.5px] font-semibold transition border',
        display
          ? 'text-neutral-700 bg-neutral-50 border-neutral-200 hover:border-neutral-400'
          : 'text-neutral-400 italic bg-neutral-50 border-dashed border-neutral-300 hover:text-neutral-700 hover:border-neutral-400',
      )}
      title={display ? 'Change close date' : 'Set estimated close date — drives the monthly filter'}
    >
      {display || 'Set date'}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* ChannelCell — per-deal Broker / Correspondent picker.              */
/* ------------------------------------------------------------------ */
function ChannelCell({ lead }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = lead.loanChannel || null;
  const label = current === 'broker' ? 'Broker' : current === 'correspondent' ? 'Corr.' : 'Set';

  const choose = async (value) => {
    setOpen(false);
    if (value === current) return;
    const updated = await LeadsStore.update(lead.id, { loanChannel: value });
    if (updated) {
      const niceLabel = value === 'broker' ? 'Broker' : 'Correspondent';
      toast.success(`${updated.name} → ${niceLabel} channel`);
    } else {
      toast.error('Could not update channel');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`pipeline-channel-${lead.id}`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11.5px] font-semibold transition',
          current
            ? current === 'broker'
              ? 'bg-blue-50 text-blue-700 border-blue-200'
              : 'bg-violet-50 text-violet-700 border-violet-200'
            : 'bg-neutral-50 text-neutral-400 border-dashed border-neutral-300 hover:text-neutral-700',
        )}
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div
          data-testid={`pipeline-channel-menu-${lead.id}`}
          className="absolute right-0 top-full mt-1.5 z-30 w-[160px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          {CHANNELS.map((c) => {
            const active = c.value === current;
            return (
              <button
                key={c.value}
                data-testid={`pipeline-channel-option-${lead.id}-${c.value}`}
                onClick={() => choose(c.value)}
                className={cn(
                  'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition flex items-center justify-between',
                  active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                <span>{c.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Kanban view — columns map 1:1 to the 5 active loan stages.          */
/* ------------------------------------------------------------------ */
// Map a lead's current pipeline stage → the `stageTimestamps.<milestone>`
// field that captures when the lead ENTERED that stage. Used to derive
// "days in current stage" on the Kanban card without any extra Mongo
// round-trip. Best-effort — `clear_to_close` and `approved_with_conditions`
// share the `locked` stamp because we don't distinguish those two stages
// in the transition timestamps yet.
const STAGE_TIMESTAMP_KEY = {
  ready_to_submit:          'application',
  submitted_to_uw:          'submission',
  approved_with_conditions: 'locked',
  clear_to_close:           'locked',
  funded:                   'funded',
};

function daysInCurrentStage(lead) {
  const key = STAGE_TIMESTAMP_KEY[normalizeStageKey(lead.loanStatus)];
  const iso = lead?.stageTimestamps?.[key]
    || lead?.submittedAt
    || lead?.claimedAt;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return null;
  const days = Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
  return days;
}

function processorFor(lead) {
  // `processor` is a free-form string on the lead doc (see leads.py Lead
  // model). Values include a real name, "self" for self-processing, or
  // empty/null when unassigned. Normalize the display so the LO sees
  // something friendly instead of the raw slug.
  const p = (lead?.processor || '').trim();
  if (!p) return null;
  if (p.toLowerCase() === 'self') return 'Self Processing';
  return p;
}

function PipelineKanban({ leads }) {
  const grouped = LOAN_STAGES
    .filter((stage) => stage.key !== 'funded')
    .map((stage) => {
      const list = leads.filter((l) => normalizeStageKey(l.loanStatus) === stage.key);
      const value = list.reduce((s, l) => s + (Number(l.estAmount) || 0), 0);
      return { stage, list, value };
    });
  return (
    <div className="overflow-x-auto -mx-6 lg:-mx-10 px-6 lg:px-10 pb-4">
      <div className="flex gap-3 min-w-max">
        {grouped.map(({ stage, list, value }) => (
          <div key={stage.key} className="w-[280px] shrink-0">
            <div className="rounded-t-xl border hairline border-b-0 bg-white px-3 py-2.5">
              <div className="flex items-center justify-between">
                <LoanStageBadge value={stage.key} />
                <span className="text-[10px] text-neutral-500 bg-neutral-100 rounded px-1.5 py-0.5 font-bold tabular-nums">{list.length}</span>
              </div>
              <div className="text-[10.5px] text-neutral-500 mt-1.5 tabular-nums font-semibold">${(value / 1000).toFixed(0)}K</div>
            </div>
            <div className="rounded-b-xl border hairline bg-neutral-50/60 p-2 space-y-2 min-h-[400px]">
              {list.map((l) => {
                const lender = lenderFor(l);
                const processor = processorFor(l);
                const days = daysInCurrentStage(l);
                return (
                  <Link
                    key={l.id}
                    to={`/leads/${l.id}`}
                    data-testid={`pipeline-kanban-card-${l.id}`}
                    className="block rounded-lg border hairline bg-white p-3 card-lift"
                  >
                    <div className="text-[12.5px] font-bold text-neutral-900 truncate leading-tight">{l.name}</div>
                    <div className="text-[10.5px] text-neutral-500 mt-0.5 truncate">
                      {loanPurposeLabel(l)}{lender ? ` · ${lender}` : ''}
                    </div>
                    {processor && (
                      <div
                        className="text-[10.5px] text-neutral-500 mt-0.5 truncate"
                        data-testid={`pipeline-kanban-processor-${l.id}`}
                      >
                        Processor: <span className="text-neutral-700 font-medium">{processor}</span>
                      </div>
                    )}
                    <div className="mt-2.5 flex items-baseline justify-between">
                      <span className="text-[14px] font-bold text-neutral-900 tracking-tight tabular-nums">{money(l.estAmount)}</span>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] text-neutral-500"
                        data-testid={`pipeline-kanban-days-${l.id}`}
                        title={days == null ? 'No stage-entry timestamp' : `${days} day${days === 1 ? '' : 's'} in current stage`}
                      >
                        <Clock className="h-3 w-3" />
                        {days == null ? '—' : `${days}d`}
                      </span>
                    </div>
                  </Link>
                );
              })}
              {list.length === 0 && (
                <div className="h-20 border border-dashed border-neutral-300 rounded-lg flex items-center justify-center text-[11px] text-neutral-400">
                  No deals here
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* StagePicker — inline stage advancement straight off the pipeline   */
/* row. Clicking the badge opens a list of all 5 stages.              */
/* "Funded" specifically gates behind a confirm modal so the broker   */
/* eyeballs the closing date one last time before the deal migrates   */
/* off the pipeline into the Fundings ledger.                         */
/* ------------------------------------------------------------------ */
function StagePicker({ lead }) {
  const [open, setOpen] = useState(false);
  const [confirmFunded, setConfirmFunded] = useState(false);
  // ── Menu positioning ────────────────────────────────────────────────
  // The picker sits inside the Pipeline LIST table, which has
  // `overflow-x-auto`. An absolutely-positioned `<div>` gets clipped by
  // that ancestor, so the "Advance to…" list was unreachable when the
  // row sat near the bottom of the table. Portal + viewport-relative
  // `position: fixed` escapes every ancestor's overflow rectangle.
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });

  const recomputePos = React.useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const MENU_H = 6 * 40 + 40; // ~6 stages @ ~40px + header
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < MENU_H + 16 && rect.top > MENU_H;
    setPos({
      top: openUp ? Math.max(8, rect.top - MENU_H - 6) : rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 240)),
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recomputePos();
    const onClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => recomputePos();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, recomputePos]);

  const currentKey = normalizeStageKey(lead.loanStatus);

  const change = async (nextKey) => {
    setOpen(false);
    if (nextKey === currentKey) return;
    // Funded is a one-way trapdoor into the Fundings ledger — confirm first.
    if (nextKey === 'funded') {
      setConfirmFunded(true);
      return;
    }
    const updated = await LeadsStore.update(lead.id, { loanStatus: nextKey });
    if (updated) {
      const stage = LOAN_STAGES.find((s) => s.key === nextKey);
      toast.success(`${updated.name} → ${stage?.label || nextKey}`);
    } else {
      toast.error('Could not change stage');
    }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        data-testid={`pipeline-stage-trigger-${lead.id}`}
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex items-center gap-1 rounded-full hover:opacity-90 transition"
        title="Change stage"
      >
        <LoanStageBadge value={lead.loanStatus} uniform />
        <ChevronDown className="h-3 w-3 text-neutral-400 group-hover:text-neutral-700 transition" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid={`pipeline-stage-menu-${lead.id}`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 220, zIndex: 9999 }}
          className="rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft">
            Advance to…
          </div>
          {LOAN_STAGES.map((s) => {
            const active = s.key === currentKey;
            return (
              <button
                key={s.key}
                data-testid={`pipeline-stage-option-${lead.id}-${s.key}`}
                onClick={() => change(s.key)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-left transition',
                  active ? 'bg-neutral-50' : 'hover:bg-neutral-50',
                )}
              >
                <LoanStageBadge value={s.key} />
                {active && <Check className="ml-auto h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
      {confirmFunded && (
        <FundedConfirmModal lead={lead} onClose={() => setConfirmFunded(false)} />
      )}
    </div>
  );
}

